/**
 * Toast próprio. O `sonner` custa ~12 KB gz e traz swipe, promise-toast, stacks
 * e portais que o balcão nunca usa — aqui a mensagem some sozinha e ninguém
 * arrasta nada com o dedo. As cores seguem o mapa único do DS: erro vermelho
 * (`--bad`), aviso amarelo (`--warn`), sucesso verde (`--ok`).
 *
 * VOS Movimento (26/09): o aviso sobe da base (receita `toast`), sai
 * descendo e encolhendo, e a pilha anda pela mola `layout` quando um some.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, m } from "motion/react";
import { useMovimento } from "@vertex/ui/movimento";

type ToastKind = "success" | "error" | "warning" | "info";
interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

type Notify = (message: string, kind?: ToastKind) => void;

const ToastCtx = createContext<Notify>(() => {});

/** Dispara um toast de qualquer lugar da árvore. */
export function useToast(): Notify {
  return useContext(ToastCtx);
}

const TINT: Record<ToastKind, string> = {
  success: "var(--ok)",
  error: "var(--bad)",
  warning: "var(--warn)",
  info: "var(--info)",
};

const DURATION: Record<ToastKind, number> = {
  /* Erro fica mais tempo: é o único que o operador PRECISA ler antes de agir. */
  success: 2600,
  info: 3200,
  warning: 5000,
  error: 6500,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const mov = useMovimento();
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(0);

  const notify = useCallback<Notify>((message, kind = "info") => {
    const id = ++seq.current;
    setItems((prev) => [...prev.slice(-3), { id, kind, message }]);
    window.setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), DURATION[kind]);
  }, []);

  /* `notify` é estável (useCallback sem deps), então o value não precisa de
     memo por dependência — mas o objeto sim, senão todo render remonta os
     consumidores. */
  const value = useMemo(() => notify, [notify]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        <AnimatePresence initial={false} mode="popLayout">
          {items.map((t) => (
            <m.div
              key={t.id}
              role={t.kind === "error" ? "alert" : "status"}
              className="pdv-toast"
              style={{ ["--chip-c" as string]: TINT[t.kind] }}
              layout={mov.layout ? "position" : false}
              transition={{ layout: mov.mola("layout") }}
              initial={mov.r.toast.initial}
              animate={mov.r.toast.animate}
              exit={mov.r.toast.exit}
            >
              {t.message}
            </m.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastCtx.Provider>
  );
}
